import { useState, useEffect, useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowUp, ArrowDown, ChartLineUp, DownloadSimple } from "@phosphor-icons/react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

export default function UsageMetrics() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [firms, setFirms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortConfig, setSortConfig] = useState({ key: "activation_date", direction: "desc" });
  const [isScrolled, setIsScrolled] = useState(false);
  const [viewMode, setViewMode] = useState("grafana"); // "grafana" or "mixpanel"
  const [mixpanelPeriod, setMixpanelPeriod] = useState("itd"); // today, yesterday, 7, 30, 90, 180, 365, itd
  
  // Get filters from URL params
  const searchQuery = searchParams.get("search") || "";
  const tierFilter = searchParams.get("tier") || "all";
  
  const setSearchQuery = (value) => {
    const params = new URLSearchParams(searchParams);
    if (value) params.set("search", value);
    else params.delete("search");
    setSearchParams(params, { replace: true });
  };
  
  const setTierFilter = (value) => {
    const params = new URLSearchParams(searchParams);
    if (value && value !== "all") params.set("tier", value);
    else params.delete("tier");
    setSearchParams(params, { replace: true });
  };

  useEffect(() => {
    api.get("/usage-metrics").then((r) => {
      setFirms(r.data.firms);
    }).finally(() => setLoading(false));
  }, []);

  // Transform column key to display label
  const formatColumnName = (key) => {
    return key
      .split("_")
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  // Get Grafana columns from first firm (exclude url since it's now part of firm_name display)
  const grafanaColumns = useMemo(() => {
    if (firms.length === 0) return [];
    const firstFirm = firms[0];
    const grafanaData = firstFirm.grafana || {};
    
    return [
      { key: "firm_name", label: "Firm Name", sortable: true, frozen: true },
      { key: "num_users", label: "Users", sortable: false },
      { key: "tier", label: "Tier", sortable: false },
      { key: "activation_date", label: "Activation Date", sortable: true },
      ...Object.keys(grafanaData)
        .map(k => ({ key: k, label: formatColumnName(k), sortable: true }))
    ];
  }, [firms]);

  // Define Mixpanel columns (fixed structure)
  const mixpanelColumns = [
    { key: "firm_name", label: "Firm Name", sortable: true, frozen: true },
    { key: "num_users", label: "Users", sortable: false },
    { key: "tier", label: "Tier", sortable: false },
    { key: "activation_date", label: "Activation Date", sortable: true },
    { key: "Total Events", label: "Total Events", sortable: true },
    { key: "Import Books", label: "Import Books", sortable: true },
    { key: "Ledger Classification", label: "Ledger Classification", sortable: true },
    { key: "Statement Generation", label: "Statement Generation", sortable: true },
    { key: "Ledger Scrutiny", label: "Ledger Scrutiny", sortable: true },
    { key: "Issues Raised", label: "Issues Raised", sortable: true },
    { key: "Doc Request Raised", label: "Doc Request Raised", sortable: true },
    { key: "Workflow Actions", label: "Workflow Actions", sortable: true },
  ];

  const columns = viewMode === "grafana" ? grafanaColumns : mixpanelColumns;

  // Process firms data based on view mode
  const processedFirms = useMemo(() => {
    return firms.map(firm => {
      if (viewMode === "grafana") {
        // Flatten Grafana data
        return {
          firm_name: firm.firm_name,
          url: firm.url,
          num_users: firm.num_users,
          tier: firm.tier,
          activation_date: firm.activation_date,
          ...firm.grafana
        };
      } else {
        // Map period to database key
        const periodKeyMap = {
          "today": "1",
          "yesterday": "1", // Will calculate as difference
          "7": "7",
          "30": "30",
          "90": "90",
          "180": "180",
          "365": "365",
          "itd": "itd"
        };
        
        const dbKey = periodKeyMap[mixpanelPeriod];
        const eventCounts = firm.mixpanel?.[dbKey] || {};
        
        // For yesterday, calculate from 7-day and 1-day data
        let processedCounts = { ...eventCounts };
        if (mixpanelPeriod === "yesterday") {
          const counts1d = firm.mixpanel?.["1"] || {};
          const counts7d = firm.mixpanel?.["7"] || {};
          // Yesterday = (7d - 1d) / 6 (approximate)
          Object.keys(counts7d).forEach(key => {
            const val7d = counts7d[key] || 0;
            const val1d = counts1d[key] || 0;
            processedCounts[key] = Math.max(0, Math.round((val7d - val1d) / 6));
          });
        }
        
        return {
          firm_name: firm.firm_name,
          url: firm.url,
          num_users: firm.num_users,
          tier: firm.tier,
          activation_date: firm.activation_date,
          "Total Events": processedCounts["Total Events"] || 0,
          "Import Books": processedCounts["Import Books"] || 0,
          "Ledger Classification": processedCounts["Ledger Classification"] || 0,
          "Statement Generation": processedCounts["Statement Generation"] || 0,
          "Ledger Scrutiny": processedCounts["Ledger Scrutiny"] || 0,
          "Issues Raised": processedCounts["Issues Raised"] || 0,
          "Doc Request Raised": processedCounts["Doc Request Raised"] || 0,
          "Workflow Actions": processedCounts["Workflow Actions"] || 0,
        };
      }
    });
  }, [firms, viewMode, mixpanelPeriod]);

  // Filter firms by search query and tier
  const filteredFirms = useMemo(() => {
    let result = processedFirms;
    
    // Filter by search query
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(f =>
        f.firm_name?.toLowerCase().includes(query) ||
        f.url?.toLowerCase().includes(query)
      );
    }
    
    // Filter by tier
    if (tierFilter && tierFilter !== "all") {
      result = result.filter(f => f.tier === tierFilter);
    }
    
    return result;
  }, [processedFirms, searchQuery, tierFilter]);

  // Sort firms
  const sortedFirms = useMemo(() => {
    if (!sortConfig.key) return filteredFirms;
    
    const sorted = [...filteredFirms].sort((a, b) => {
      const aVal = a[sortConfig.key];
      const bVal = b[sortConfig.key];
      
      // Handle null/undefined
      if (aVal == null && bVal == null) return 0;
      if (aVal == null) return 1;
      if (bVal == null) return -1;
      
      // Numeric comparison for numbers
      if (typeof aVal === "number" && typeof bVal === "number") {
        return sortConfig.direction === "asc" ? aVal - bVal : bVal - aVal;
      }
      
      // String comparison using localeCompare for proper alphabetical sorting
      const aStr = String(aVal);
      const bStr = String(bVal);
      const compareResult = aStr.localeCompare(bStr, undefined, { sensitivity: 'base' });
      return sortConfig.direction === "asc" ? compareResult : -compareResult;
    });
    
    return sorted;
  }, [filteredFirms, sortConfig]);

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc"
    }));
  };

  // Export to CSV
  const handleExport = () => {
    if (sortedFirms.length === 0) return;
    
    // Build CSV header
    const headers = columns.map(col => col.label).join(",");
    
    // Build CSV rows
    const rows = sortedFirms.map(firm => {
      return columns.map(col => {
        let value = firm[col.key];
        if (col.key === "firm_name") {
          // Include URL in export
          return `"${firm.firm_name || ""} (${firm.url || ""})"`;
        }
        if (typeof value === "number") {
          return value;
        }
        return `"${value || ""}"`;
      }).join(",");
    }).join("\n");
    
    const csv = `${headers}\n${rows}`;
    const blob = new Blob([csv], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `usage-metrics-${new Date().toISOString().split("T")[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  // Handle scroll detection
  const handleScroll = (e) => {
    setIsScrolled(e.target.scrollLeft > 0);
  };

  // Update sort when switching view modes
  useEffect(() => {
    if (viewMode === "mixpanel") {
      setSortConfig({ key: "Total Events", direction: "desc" });
    } else {
      setSortConfig({ key: "activation_date", direction: "desc" });
    }
  }, [viewMode]);

  if (loading) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="font-display text-3xl font-semibold tracking-tight text-gray-900 flex items-center gap-2">
            <ChartLineUp size={28} weight="fill" className="text-violet-600" />
            Usage Metrics
          </h1>
          <p className="text-sm text-gray-500 mt-1">Loading live customers...</p>
        </div>

        {/* Loading Skeletons */}
        <div className="bg-white border border-gray-200 rounded-lg p-3 flex flex-wrap gap-2 items-center">
          <div className="relative flex-1 min-w-[220px] h-10 bg-gray-200 rounded-lg animate-pulse"></div>
          <div className="w-[150px] h-10 bg-gray-200 rounded-lg animate-pulse"></div>
        </div>

        <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gradient-to-b from-violet-50 to-white border-b-2 border-violet-100">
                <tr>
                  {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
                    <th key={i} className="px-4 py-3">
                      <div className="h-4 bg-gray-300 rounded animate-pulse"></div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
                  <tr key={i} className={i % 2 === 0 ? "bg-gray-50" : "bg-white"}>
                    {[1, 2, 3, 4, 5, 6, 7, 8].map((j) => (
                      <td key={j} className="px-4 py-3">
                        <div className="h-4 bg-gray-200 rounded animate-pulse"></div>
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <div className="flex items-center gap-3">
            <ChartLineUp size={28} weight="fill" className="text-violet-600" />
            <h1 className="font-display text-3xl font-semibold tracking-tight text-gray-900">
              Usage Metrics
            </h1>
          </div>
          <p className="text-sm text-gray-500 mt-1 ml-11">
            Displaying {sortedFirms.length} live customer{sortedFirms.length !== 1 ? "s" : ""}
          </p>
        </div>

        {/* Toggle between Grafana and Mixpanel */}
        <div className="inline-flex bg-gradient-to-r from-violet-50 to-purple-50 rounded-xl p-1 shadow-md border border-violet-100">
          <button
            onClick={() => setViewMode("grafana")}
            className={`px-8 py-2.5 rounded-lg text-sm font-semibold transition-all duration-300 ${
              viewMode === "grafana"
                ? "bg-white text-violet-700 shadow-lg shadow-violet-200/50 scale-105"
                : "text-violet-600 hover:text-violet-700 hover:bg-white/50"
            }`}
          >
            Grafana
          </button>
          <button
            onClick={() => setViewMode("mixpanel")}
            className={`px-8 py-2.5 rounded-lg text-sm font-semibold transition-all duration-300 ${
              viewMode === "mixpanel"
                ? "bg-white text-violet-700 shadow-lg shadow-violet-200/50 scale-105"
                : "text-violet-600 hover:text-violet-700 hover:bg-white/50"
            }`}
          >
            Mixpanel
          </button>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="bg-white border border-gray-200 rounded-lg p-3 flex flex-wrap gap-2 items-center">
        <div className="relative flex-1 min-w-[220px]">
          <input
            type="text"
            placeholder="Filter by firm name or URL..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition-shadow"
          />
        </div>
        <Select value={tierFilter} onValueChange={(v) => setTierFilter(v)}>
          <SelectTrigger className="w-[150px]">
            <SelectValue placeholder="All tiers" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All tiers</SelectItem>
            <SelectItem value="Premium">Premium</SelectItem>
            <SelectItem value="Enterprise">Enterprise</SelectItem>
            <SelectItem value="Standard">Standard</SelectItem>
          </SelectContent>
        </Select>
        {viewMode === "mixpanel" && (
          <Select value={mixpanelPeriod} onValueChange={(v) => setMixpanelPeriod(v)}>
            <SelectTrigger className="w-[150px] border-2 border-violet-200 bg-white hover:border-violet-300 text-violet-700 font-semibold shadow-sm">
              <SelectValue placeholder="Period" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="today">Today</SelectItem>
              <SelectItem value="yesterday">Yesterday</SelectItem>
              <SelectItem value="7">7D</SelectItem>
              <SelectItem value="30">30D</SelectItem>
              <SelectItem value="90">90D</SelectItem>
              <SelectItem value="180">180D</SelectItem>
              <SelectItem value="365">365D</SelectItem>
              <SelectItem value="itd">ITD</SelectItem>
            </SelectContent>
          </Select>
        )}
        {(searchQuery || tierFilter !== "all") && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setSearchParams({}, { replace: true });
            }}
            className="text-gray-600"
          >
            Clear filters
          </Button>
        )}
        <button
          onClick={handleExport}
          disabled={sortedFirms.length === 0}
          className="flex items-center gap-2 px-3 py-1.5 border border-gray-300 hover:bg-gray-100 disabled:bg-gray-100 disabled:cursor-not-allowed text-gray-700 rounded-md transition-colors duration-200 text-sm font-medium ml-auto"
        >
          <DownloadSimple size={16} weight="regular" />
          <span>Export</span>
        </button>
      </div>

      {/* Table with Premium Styling and Sticky Header */}
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-md hover:shadow-lg transition-shadow duration-300">
        <div className="overflow-auto max-h-[calc(100vh-230px)]" onScroll={handleScroll}>
          <table className="w-full text-sm">
            <thead className="bg-gradient-to-b from-violet-50 to-white border-b-2 border-violet-100 sticky top-0 z-20">
              <tr>
                {columns.map((col) => {
                  // Check if this column has numeric values by looking at first firm
                  const firstValue = sortedFirms.length > 0 ? sortedFirms[0][col.key] : null;
                  const isNumericColumn = typeof firstValue === "number";
                  
                  return (
                    <th
                      key={col.key}
                      onClick={() => col.sortable && handleSort(col.key)}
                      className={`px-4 py-3 text-xs font-semibold text-gray-700 uppercase tracking-wider whitespace-nowrap last:border-r-0 ${
                        isNumericColumn ? "text-right" : "text-left"
                      } ${col.sortable ? "cursor-pointer hover:bg-violet-100/50 select-none transition-colors duration-150" : ""
                      } ${col.frozen ? `sticky left-0 z-30 bg-gradient-to-b from-violet-50 to-white border-r border-gray-200 ${isScrolled ? "shadow-[4px_0_8px_-2px_rgba(0,0,0,0.15)]" : ""}` : "border-r border-gray-100"}`}
                    >
                      <div className={`flex items-center gap-1 ${isNumericColumn ? "justify-end" : ""}`}>
                        {col.label}
                        {col.sortable && sortConfig.key === col.key && (
                          sortConfig.direction === "asc" ? 
                            <ArrowUp size={14} weight="bold" className="text-violet-600" /> : 
                            <ArrowDown size={14} weight="bold" className="text-violet-600" />
                        )}
                      </div>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {sortedFirms.map((firm, idx) => (
                <tr 
                  key={idx} 
                  className={`border-b border-gray-100 last:border-b-0 transition-all duration-200 ${
                    idx % 2 === 0 ? "bg-white" : "bg-gray-50"
                  } hover:bg-violet-50/30 hover:shadow-[inset_0_1px_0_0_rgba(139,92,246,0.1),inset_0_-1px_0_0_rgba(139,92,246,0.1)] hover:scale-[1.001]`}
                >
                  {columns.map((col) => {
                    const value = firm[col.key];
                    const isNumeric = typeof value === "number";
                    
                    return (
                      <td 
                        key={col.key} 
                        className={`px-4 py-3 whitespace-nowrap last:border-r-0 ${
                          isNumeric ? "text-right font-mono tabular-nums text-gray-900" : "text-gray-900"
                        } ${
                          col.frozen ? `sticky left-0 z-10 border-r border-gray-200 ${isScrolled ? "shadow-[4px_0_8px_-2px_rgba(0,0,0,0.15)]" : ""}` : "border-r border-gray-100/50"
                        } ${
                          col.frozen && idx % 2 === 0 ? "bg-white" : col.frozen && idx % 2 !== 0 ? "bg-gray-50" : ""
                        }`}
                      >
                        {col.key === "firm_name" ? (
                          <div>
                            <Link 
                              to={`/firms/${firm.url?.replace('.assureai.in', '')}?from=${encodeURIComponent(`/usage-metrics${searchParams.toString() ? `?${searchParams.toString()}` : ''}`)}`}
                              className="font-semibold text-gray-900 hover:text-violet-600 hover:underline transition-colors"
                            >
                              {firm.firm_name || "—"}
                            </Link>
                            <div className="text-xs text-gray-500 font-mono mt-0.5">{firm.url || ""}</div>
                          </div>
                        ) : isNumeric ? (
                          value.toLocaleString()
                        ) : (
                          value || "—"
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {sortedFirms.length === 0 && !loading && (
        <div className="text-center py-8 text-gray-400">
          No firms match your search.
        </div>
      )}
    </div>
  );
}
